import React, { useState, useEffect } from 'react';
import { biweeklyTimesheetAPI, payPeriodAPI } from '../services/api';
import type { BiweeklyTimesheet, BiweeklyTimesheetDetail, PayPeriod, UserTimeEntry } from '../types';

const BiweeklyApprovalQueue: React.FC = () => {
  const [timesheets, setTimesheets] = useState<BiweeklyTimesheet[]>([]);
  const [payPeriods, setPayPeriods] = useState<PayPeriod[]>([]);
  const [selectedTimesheet, setSelectedTimesheet] = useState<BiweeklyTimesheetDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [filterPeriod, setFilterPeriod] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('submitted'); // Default to pending
  
  // Reject modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingId, setRejectingId] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, [filterPeriod, filterStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch pay periods
      const periodsRes = await payPeriodAPI.getAll();
      setPayPeriods(periodsRes.data.results || []);
      
      // Fetch timesheets based on filters
      const params: any = {};
      if (filterPeriod) params.pay_period = filterPeriod;
      if (filterStatus) params.status = filterStatus;
      
      const timesheetsRes = await biweeklyTimesheetAPI.getAll(params);
      setTimesheets(timesheetsRes.data.results || []);
      
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (timesheetId: number) => {
    try {
      const response = await biweeklyTimesheetAPI.getById(timesheetId);
      setSelectedTimesheet(response.data);
    } catch (err: any) {
      console.error('Failed to fetch timesheet details:', err);
      alert(err.response?.data?.error || 'Failed to load timesheet details');
    }
  };

  const handleApprove = async (timesheetId: number) => {
    if (!window.confirm('Are you sure you want to approve this timesheet?')) {
      return;
    }
    
    try {
      await biweeklyTimesheetAPI.approve(timesheetId);
      fetchData();
      setSelectedTimesheet(null);
      alert('Timesheet approved successfully!');
    } catch (err: any) {
      console.error('Failed to approve timesheet:', err);
      alert(err.response?.data?.error || 'Failed to approve timesheet');
    }
  };

  const openRejectModal = (timesheetId: number) => {
    setRejectingId(timesheetId);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    
    if (!rejectReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    
    try {
      await biweeklyTimesheetAPI.reject(rejectingId, rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
      setRejectingId(null);
      fetchData();
      setSelectedTimesheet(null);
      alert('Timesheet rejected. The employee has been notified.');
    } catch (err: any) {
      console.error('Failed to reject timesheet:', err);
      alert(err.response?.data?.error || 'Failed to reject timesheet');
    }
  };

  const handleUnapprove = async (timesheetId: number) => {
    if (!window.confirm('Are you sure you want to return this timesheet to pending approval? This will remove the approval.')) {
      return;
    }
    
    try {
      await biweeklyTimesheetAPI.unapprove(timesheetId);
      fetchData();
      setSelectedTimesheet(null);
      alert('Timesheet returned to pending approval.');
    } catch (err: any) {
      console.error('Failed to unapprove timesheet:', err);
      alert(err.response?.data?.error || 'Failed to unapprove timesheet');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatPeriodDates = (period: PayPeriod) => {
    const start = new Date(period.start_date + 'T00:00:00');
    const end = new Date(period.end_date + 'T00:00:00');
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // Group time entries by week
  const groupEntriesByWeek = (entries: UserTimeEntry[], period: PayPeriod) => {
    if (!entries || !period) return { week1: [], week2: [] };
    
    const periodStart = new Date(period.start_date + 'T00:00:00');
    const week1End = new Date(periodStart);
    week1End.setDate(week1End.getDate() + 6); // First 7 days
    
    const week1: UserTimeEntry[] = [];
    const week2: UserTimeEntry[] = [];
    
    entries.forEach(entry => {
      const entryDate = new Date(entry.date + 'T00:00:00');
      if (entryDate <= week1End) {
        week1.push(entry);
      } else {
        week2.push(entry);
      }
    });
    
    return { week1, week2 };
  };

  const calculateTotalHours = (entries: UserTimeEntry[]) => {
    return entries.reduce((sum, entry) => sum + parseFloat(entry.hours?.toString() || '0'), 0);
  };

  const getWeekDateRange = (period: PayPeriod, weekNum: 1 | 2) => {
    if (!period) return '';
    const start = new Date(period.start_date + 'T00:00:00');
    if (weekNum === 1) {
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    } else {
      const week2Start = new Date(start);
      week2Start.setDate(week2Start.getDate() + 7);
      const end = new Date(period.end_date + 'T00:00:00');
      return `${week2Start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <h2>Biweekly Timesheet Approvals</h2>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'submitted': return 'Pending Approval';
      case 'approved': return 'Approved';
      case 'draft': return 'Draft/Rejected';
      default: return 'All';
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case 'submitted':
        return { backgroundColor: '#fff3e0', color: '#e65100' };
      case 'approved':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'draft':
        return { backgroundColor: '#e3f2fd', color: '#1565c0' };
      default:
        return { backgroundColor: '#f5f5f5', color: '#666' };
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2>Biweekly Timesheets</h2>
          <span style={{
            padding: '0.5rem 1rem',
            ...getStatusBadgeStyle(filterStatus),
            borderRadius: '4px',
            fontWeight: 'bold',
          }}>
            {timesheets.length} {getStatusLabel(filterStatus)}
          </span>
        </div>

        {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}

        {/* Filters */}
        <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: '180px' }}>
            <label htmlFor="filterStatus" style={{ marginBottom: '0.25rem' }}>Status</label>
            <select
              id="filterStatus"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="submitted">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="draft">Draft/Rejected</option>
            </select>
          </div>
          
          <div className="form-group" style={{ marginBottom: 0, minWidth: '250px' }}>
            <label htmlFor="filterPeriod" style={{ marginBottom: '0.25rem' }}>Pay Period</label>
            <select
              id="filterPeriod"
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
            >
              <option value="">All Periods</option>
              {payPeriods.map((period) => (
                <option key={period.id} value={period.id}>
                  {period.name}
                </option>
              ))}
            </select>
          </div>
          
          {(filterPeriod || filterStatus !== 'submitted') && (
            <button
              onClick={() => {
                setFilterPeriod('');
                setFilterStatus('submitted');
              }}
              className="btn btn-secondary btn-small"
            >
              Reset Filters
            </button>
          )}
        </div>

        {/* Timesheets Table */}
        {timesheets.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
          }}>
            <p style={{ fontSize: '1.1rem', color: '#666' }}>
              No biweekly timesheets found{filterStatus ? ` with status "${getStatusLabel(filterStatus)}"` : ''}.
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Pay Period</th>
                  <th>Status</th>
                  <th>Hours</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {timesheets.map((ts) => (
                  <tr key={ts.id}>
                    <td>
                      <strong>{ts.user_full_name || ts.user_username}</strong>
                      <br />
                      <span style={{ fontSize: '0.85rem', color: '#666' }}>
                        @{ts.user_username}
                      </span>
                    </td>
                    <td>
                      {ts.pay_period_details ? formatPeriodDates(ts.pay_period_details) : '-'}
                    </td>
                    <td>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        ...getStatusBadgeStyle(ts.status),
                      }}>
                        {ts.status === 'submitted' ? 'Pending' : ts.status.charAt(0).toUpperCase() + ts.status.slice(1)}
                      </span>
                      {ts.rejection_reason && (
                        <div style={{ fontSize: '0.8rem', color: '#c62828', marginTop: '0.25rem' }}>
                          Rejected
                        </div>
                      )}
                    </td>
                    <td style={{ fontWeight: 'bold' }}>
                      {parseFloat(ts.total_hours || '0').toFixed(2)}
                    </td>
                    <td>
                      {ts.status === 'approved' && ts.approved_at
                        ? new Date(ts.approved_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })
                        : ts.submitted_at
                        ? new Date(ts.submitted_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })
                        : '-'}
                    </td>
                    <td className="actions">
                      <button
                        onClick={() => handleViewDetails(ts.id!)}
                        className="btn btn-secondary btn-small"
                      >
                        View
                      </button>
                      {ts.status === 'submitted' && (
                        <>
                          <button
                            onClick={() => handleApprove(ts.id!)}
                            className="btn btn-success btn-small"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => openRejectModal(ts.id!)}
                            className="btn btn-danger btn-small"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {ts.status === 'approved' && (
                        <button
                          onClick={() => handleUnapprove(ts.id!)}
                          className="btn btn-warning btn-small"
                        >
                          Unapprove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {selectedTimesheet && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedTimesheet(null);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <div>
                <h2 style={{ marginBottom: '0.5rem' }}>Timesheet Details</h2>
                <p style={{ margin: 0, color: '#666' }}>
                  {selectedTimesheet.user_full_name || selectedTimesheet.user_username}
                  {selectedTimesheet.pay_period_details && (
                    <> • {formatPeriodDates(selectedTimesheet.pay_period_details)}</>
                  )}
                </p>
              </div>
              <button
                onClick={() => setSelectedTimesheet(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  padding: '0.25rem 0.5rem',
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              padding: '1rem',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              display: 'flex',
              justifyContent: 'space-around',
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1976d2' }}>
                  {parseFloat(selectedTimesheet.total_hours || '0').toFixed(2)}
                </div>
                <div style={{ color: '#666' }}>Total Hours</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#388e3c' }}>
                  {selectedTimesheet.entries_count || 0}
                </div>
                <div style={{ color: '#666' }}>Entries</div>
              </div>
            </div>

            {/* Time Entries by Week */}
            {selectedTimesheet.time_entries && selectedTimesheet.time_entries.length > 0 && selectedTimesheet.pay_period_details ? (
              <>
                {(() => {
                  const { week1, week2 } = groupEntriesByWeek(
                    selectedTimesheet.time_entries, 
                    selectedTimesheet.pay_period_details
                  );
                  const week1Total = calculateTotalHours(week1);
                  const week2Total = calculateTotalHours(week2);
                  
                  return (
                    <>
                      {/* Week 1 */}
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ 
                          marginBottom: '0.5rem', 
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e3f2fd',
                          borderRadius: '4px',
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}>
                          <span>Week 1</span>
                          <span style={{ fontWeight: 'normal', color: '#666' }}>
                            {getWeekDateRange(selectedTimesheet.pay_period_details!, 1)}
                          </span>
                        </h4>
                        {week1.length > 0 ? (
                          <table>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Project</th>
                                <th>Rate</th>
                                <th style={{ textAlign: 'right' }}>Hours</th>
                                <th>Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {week1
                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                .map((entry) => (
                                  <tr key={entry.id}>
                                    <td>{formatDate(entry.date)}</td>
                                    <td>{entry.project_code}</td>
                                    <td>{entry.billing_rate_name}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                      {parseFloat(entry.hours?.toString() || '0').toFixed(2)}
                                    </td>
                                    <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {entry.notes || '-'}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                            <tfoot>
                              <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                <td colSpan={3}>Week 1 Total</td>
                                <td style={{ textAlign: 'right' }}>{week1Total.toFixed(2)}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        ) : (
                          <p style={{ color: '#999', textAlign: 'center', padding: '0.5rem' }}>
                            No time entries for Week 1
                          </p>
                        )}
                      </div>

                      {/* Week 2 */}
                      <div>
                        <h4 style={{ 
                          marginBottom: '0.5rem', 
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e3f2fd',
                          borderRadius: '4px',
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}>
                          <span>Week 2</span>
                          <span style={{ fontWeight: 'normal', color: '#666' }}>
                            {getWeekDateRange(selectedTimesheet.pay_period_details!, 2)}
                          </span>
                        </h4>
                        {week2.length > 0 ? (
                          <table>
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Project</th>
                                <th>Rate</th>
                                <th style={{ textAlign: 'right' }}>Hours</th>
                                <th>Notes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {week2
                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                .map((entry) => (
                                  <tr key={entry.id}>
                                    <td>{formatDate(entry.date)}</td>
                                    <td>{entry.project_code}</td>
                                    <td>{entry.billing_rate_name}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                                      {parseFloat(entry.hours?.toString() || '0').toFixed(2)}
                                    </td>
                                    <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {entry.notes || '-'}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                            <tfoot>
                              <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                <td colSpan={3}>Week 2 Total</td>
                                <td style={{ textAlign: 'right' }}>{week2Total.toFixed(2)}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        ) : (
                          <p style={{ color: '#999', textAlign: 'center', padding: '0.5rem' }}>
                            No time entries for Week 2
                          </p>
                        )}
                      </div>
                    </>
                  );
                })()}
              </>
            ) : (
              <p style={{ textAlign: 'center', color: '#666' }}>No time entries found.</p>
            )}

            {/* Status Info & Action Buttons */}
            <div style={{
              marginTop: '1.5rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid #ddd',
            }}>
              {selectedTimesheet.status === 'approved' && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#e8f5e9',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <div>
                    <strong style={{ color: '#2e7d32' }}>✓ Approved</strong>
                    {selectedTimesheet.approved_by_username && (
                      <p style={{ margin: '0.5rem 0 0 0', color: '#666' }}>
                        by {selectedTimesheet.approved_by_username}
                        {selectedTimesheet.approved_at && (
                          <> on {new Date(selectedTimesheet.approved_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}</>
                        )}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleUnapprove(selectedTimesheet.id!)}
                    className="btn btn-warning"
                  >
                    Return to Pending
                  </button>
                </div>
              )}

              {selectedTimesheet.status === 'draft' && selectedTimesheet.rejection_reason && (
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#ffebee',
                  borderRadius: '8px',
                  marginBottom: '1rem',
                }}>
                  <strong style={{ color: '#c62828' }}>Rejected</strong>
                  <p style={{ margin: '0.5rem 0 0 0' }}>
                    <strong>Reason:</strong> {selectedTimesheet.rejection_reason}
                  </p>
                </div>
              )}

              {selectedTimesheet.status === 'submitted' && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '1rem',
                }}>
                  <button
                    onClick={() => openRejectModal(selectedTimesheet.id!)}
                    className="btn btn-danger"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleApprove(selectedTimesheet.id!)}
                    className="btn btn-success"
                  >
                    Approve
                  </button>
                </div>
              )}

              {selectedTimesheet.status !== 'submitted' && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setSelectedTimesheet(null)}
                    className="btn btn-secondary"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowRejectModal(false);
              setRejectReason('');
              setRejectingId(null);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
            }}
          >
            <h2 style={{ marginBottom: '1rem', color: '#c62828' }}>Reject Timesheet</h2>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              Please provide a reason for rejection. This will be shown to the employee.
            </p>
            <div className="form-group">
              <label htmlFor="rejectReason">Rejection Reason *</label>
              <textarea
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                placeholder="Enter reason for rejection..."
                required
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                  setRejectingId(null);
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="btn btn-danger"
                disabled={!rejectReason.trim()}
              >
                Reject Timesheet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BiweeklyApprovalQueue;
